/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit, Optional} from '@angular/core';
import {ControlContainer} from '@angular/forms';
import {BsDropdownDirective} from 'ngx-bootstrap/dropdown';
import * as _ from 'lodash';

import template from './assets.component.html';

import {MEDIA_TYPE, MediaTypeHelper} from '../../../components/util/media-type-helper';
import {Uuid} from '../../../components/util/uuid';
import {
  buildFileLimitExceededWarning,
  buildUploadWarnings,
  toUploadableFiles,
  UploadableFile
} from '../../common/directives/file-upload/file-upload.util';
import {AssetService} from '../../../components/api/resources/resource/asset/asset.service';
import {setFormValidity} from '../../common/controller/form-validity';

const FILE_UPLOAD_LIMIT = 10;
const DEFAULT_ACCEPT = '.webp,.svg,.jpg,.png,.jpeg,.gif,.zip,.swf,.html,.htm,.js,.txt,.json,.css,.mp4,.webm,.mp3,.vtt,.srt,.otf';

@Component({
  standalone: false,
  selector: 'adn-assets',
  template
})
export class AssetsComponent implements OnInit {
  @Input() creative: any;
  @Input() layout: any;
  @Input() name = 'assets';
  @Input() accept = DEFAULT_ACCEPT;
  @Input() eventHook: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() maxFiles: number;
  @Input() doHelpful: boolean;

  parentObject: any;
  allFilesProcessed = true;
  allFilesProgress = 0;
  validationWarnings: any[] = [];
  filesFailedUpload: UploadableFile[] = [];
  isDragOver = false;

  private fileUploadLimit: number = FILE_UPLOAD_LIMIT;
  private previousAssets: any[] = [];
  private creativePreSave = false;

  constructor(
    @Inject(AssetService) private readonly Asset: any,
    @Optional() @Inject(ControlContainer) private readonly parentForm: ControlContainer,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.name = this.name || 'assets';
    this.accept = this.accept || DEFAULT_ACCEPT;
    this.parentObject = this.creative || this.layout;
    if (!this.parentObject) {
      return;
    }

    this.fileUploadLimit = this.maxFiles || FILE_UPLOAD_LIMIT;

    this.parentObject.assets = this.parentObject.assets || [];
    this.assessAssets(true);

    this.eventHook.onCreativeSave = () => {
      this.assessAssets(true);
    };
    this.eventHook.onAssetUpdate = (afterSave?: boolean) => {
      this.assessAssets(afterSave);
    };
  }

  visibleAssets(): any[] {
    const assets = _.filter(this.parentObject.assets, (asset) => {
      return asset.objectState !== 'HIDDEN' && asset.hideMe !== true;
    });
    return this.maxFiles ? _.take(assets, this.maxFiles) : assets;
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.upload(toUploadableFiles(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    this.upload(toUploadableFiles(event.dataTransfer?.files));
  }

  assign(asset: any, tag: any, dropdown?: BsDropdownDirective): void {
    this.eventHook.assignAsset(asset, tag);
    this.assessAssets();
    dropdown?.hide();
  }

  cancel(asset: any): void {
    const previousAsset = _.find(this.previousAssets, ['id', asset.id]);
    const currentAsset = _.find(this.parentObject.assets, ['id', asset.id]);
    if (previousAsset && currentAsset) {
      currentAsset.fileName = previousAsset.fileName;
    }
  }

  edit(asset: any): void {
    this.previousAssets = _.reject(this.previousAssets, ['id', asset.id]);
    this.previousAssets.push(_.cloneDeep(asset));
  }

  delete(asset: any): void {
    asset.objectState = 'HIDDEN';
    if (asset.primaryHtmlAsset) {
      const assetIdMatch = asset.id.substring(0, asset.id.length - 3);
      const relatedAssets = _.filter(this.parentObject.assets, (a) => {
        return _.startsWith(a.id, assetIdMatch) && a.id !== asset.id;
      });
      _.forEach(relatedAssets, (a) => {
        this.Asset.update(this.parentObject.id, {objectState: asset.objectState}, a.id);
      });
    }
    this.Asset.update(this.parentObject.id, {objectState: asset.objectState}, asset.id).then(() => {
      this.eventHook.onUpdate();
    });
  }

  save(asset: any): void {
    this.Asset.update(this.parentObject.id, {fileName: asset.fileName}, asset.id);
  }

  private assessAssets(afterSave?: boolean): void {
    _.forEach(this.parentObject.assets, (asset) => {
      asset.hideMe = asset.htmlCreativeType && !asset.primaryHtmlAsset;

      let assignedTag: any = false;
      _.forEach(this.parentObject.cToAssets || [], (assignedAsset, tag) => {
        if (assignedAsset && asset && assignedAsset.id === asset.id) {
          assignedTag = tag;
        }
      });
      asset.assigned = assignedTag;

      if (afterSave) {
        asset.assignedAndSaved = assignedTag;
      }

      const layoutId = _.get(this.parentObject, ['layout', 'id']);
      if (layoutId && asset.matchingAcs) {
        asset.possibleTags = asset.matchingAcs[this.parentObject.layout.id];
      }
    });
  }

  private updateWarnings(paramWarnings?: any): void {
    if (paramWarnings) {
      this.validationWarnings = this.validationWarnings.concat(_.isArray(paramWarnings) ? paramWarnings : [paramWarnings]);
    } else {
      this.validationWarnings = [];
    }
  }

  private upload(allFiles: UploadableFile[]): void {
    this.filesFailedUpload = [];
    this.updateWarnings();

    if (!allFiles || !_.isArray(allFiles) || allFiles.length < 1) {
      setFormValidity(this.parentForm, 'upload', false);
      return;
    }

    const files = _.clone(_.slice(allFiles, 0, this.fileUploadLimit));
    if (allFiles.length > this.fileUploadLimit) {
      this.updateWarnings(buildFileLimitExceededWarning(this.fileUploadLimit, allFiles.length, false));
    }
    allFiles = null;

    // add a pending validation
    setFormValidity(this.parentForm, 'upload');
    this.allFilesProcessed = false;
    this.allFilesProgress = 0;
    this.cdr.markForCheck();

    const preUpdatePromises = [];
    if ((this.creative && !this.creative.id) || (this.creative && this.creative.id && this.isNew && !this.creativePreSave)) {
      this.creative.id = this.creative.id || Uuid.generate();
      this.creative.name = this.creative.name || "Creative"; // need a name otherwise no save is possible

      if (!_.get(this.creative, ['lineItem', 'id'])) {
        delete this.creative.lineItem;
      }

      // need to do this to enable a second creative being used.
      this.parentObject = this.creative;
      const creativeToSave = _.cloneDeep(this.creative);
      preUpdatePromises.push(creativeToSave.$save());
      this.creativePreSave = true;
    }

    Promise.all(preUpdatePromises).then(() => {
      let hasZip = false;

      const setAllFilesProgress = () => {
        const progress = _.reduce(files, (result, file) => {
          return result + (_.isFinite(file.progress) ? file.progress : 0);
        }, 0);
        this.allFilesProgress = Math.min(100, parseInt(String(progress / files.length), 10));
        this.cdr.markForCheck();
      };
      const allFilesChecks = () => {
        if (!_.find(files, ['processed', false])) {
          this.allFilesProcessed = true;
        }

        // if at least one file is valid, then say the upload is a success
        setFormValidity(this.parentForm, 'upload', _.some(files, 'validity'));

        this.filesFailedUpload = _.filter(files, ['validity', false]);
      };
      const refreshAssetsAfterZipCheck = () => {
        if (hasZip && this.allFilesProcessed && this.creative) {
          return this.Asset.fromCreative(this.creative.id).then((assets) => {
            this.creative.assets = assets;
            this.assessAssets();
          });
        }
      };

      // TODO: when/if the API supports it, should do multi-file upload with one request.
      _.forEach(files, (file) => {
        file.processed = false;
        file.validity = undefined;

        const parentId = this.creative ? this.creative.id : this.layout.id;
        const onProgress = (progressEvent) => {
          this.ngZone.run(() => {
            file.progress = Math.min(100, parseInt(String(100 * progressEvent.loaded / progressEvent.total), 10));
            setAllFilesProgress();
          });
        };
        const uploadPromise = this.doHelpful
          ? this.Asset.helpfulBulkUpload(parentId, {file: file}, onProgress)
          : this.Asset.upload(parentId, {file: file}, undefined, onProgress);
        uploadPromise.then((assets) => {
          this.ngZone.run(() => {
            file.processed = true;
            file.validity = true;

            const mappedAssets = _.flatten(_.map(assets, (a) => {
              return a.results ? a.results : a;
            }));
            this.parentObject.assets = this.parentObject.assets || [];
            let allAssets = this.parentObject.assets.concat(mappedAssets);
            if (this.maxFiles && allAssets.length > this.maxFiles) {
              allAssets = _.takeRight(allAssets, this.maxFiles);
            }

            const isZip = MediaTypeHelper.isMediaType(file.type, MEDIA_TYPE.zip);
            if (!isZip) {
              this.parentObject.assets = allAssets;
            }
            hasZip = hasZip || isZip;

            allFilesChecks();
            const promise = refreshAssetsAfterZipCheck();
            Promise.resolve(promise).then(() => {
              this.eventHook.onUpdate(assets);
            });
            this.assessAssets();
          });
        }, (error) => {
          this.ngZone.run(() => {
            file.processed = true;
            file.validity = false;
            this.updateWarnings(buildUploadWarnings(error, {mode: 'cdn-asset'}));
            allFilesChecks();
          });
        });
      });
    }, () => {
      this.parentObject.id = undefined;
      setFormValidity(this.parentForm, 'upload', false);
    });
  }

}
